import { Injectable } from '@angular/core';
import { Observable, Subject, Subscriber, timer, Subscription } from 'rxjs';
import { RestApi } from './rest.api';
import { OAuthClient } from './oauth.client';
import { WSFederationClient } from './wsfederation.client';
import { AutoLoginClient } from './auto-login.client';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfileActions } from 'app/core/reducers/profile.actions';
import { AuthenticationTypeValidationSevice } from 'app/shared/services/authentication-type-validation.service';
import { AuthStrategy } from './AuthStrategy';
import { AutoLoginStrategy } from './AutoLoginStrategy';
import { Accounts2ClientService } from './accounts2.client';
import { map, throttleTime } from 'rxjs/operators';
import { AuthType, GetAuthenticationTypeModel } from './typings/AuthClient';
import { extractSyncCode } from 'bz-authentication-client/lib/common';
import { Store } from '@ngrx/store';
import { GetAuthenticationIsValid } from '../security/security-authentication/store/actions/authentication.actions';
import { BzgModalService, BzgModalRef } from 'bz-genome-angular';
import { ExpirationSessionModalComponent } from './expiration-session-modal/expiration-session-modal.component';
import { IAppState } from 'app/IAppState';
import { IConfigurationSummary } from 'app/core/typings/IConfigurationSummary';

@Injectable()
export class AuthClient {
  public authType: AuthType;
  public authenticationConfig: GetAuthenticationTypeModel;
  public sessionUpdate: Subject<void> = new Subject<void>();

  private params: any;
  private expirationTimer: Observable<number>;
  private endingTimer: Observable<number>;
  private modalSession: BzgModalRef;
  private expirationTimerUnsubscribe: Subject<void> = new Subject<void>();
  private _authenticationSubject: Subject<boolean>;
  private _authenticationTypeSubject: Subject<string>;
  private _loggedIn: boolean;
  private authStrategy: AuthStrategy;
  private intervalTimeOut: number | null = null;

  constructor(
    private _api: RestApi,
    private _oAuthClient: OAuthClient,
    private _autoLoginClient: AutoLoginClient,
    private _wsFederationClient: WSFederationClient,
    private _router: Router,
    private _route: ActivatedRoute,
    private _profileActions: ProfileActions,
    private _modalService: BzgModalService,
    private _authenticationTypeValidationService: AuthenticationTypeValidationSevice,
    private readonly accounts2Service: Accounts2ClientService,
    private _store: Store<IAppState>
  ) {
    this.params = {};
    this._authenticationTypeSubject = new Subject<string>();
    this._authenticationSubject = new Subject<boolean>();
    this._authenticationSubject.asObservable().subscribe(data => {
      this._loggedIn = data;
      if (this._loggedIn === true) {
        this.getProfile().subscribe((profile: IUserProfile) => {
          this._profileActions.Update(profile);
        });
        // this._store.dispatch(new GetAuthenticationIsValid());
      } else {
        this.expirationTimerUnsubscribe.next();
      }
    });
  }

  resetInterval() {
    this.intervalTimeOut = setTimeout(() => {
      const timeExpiration = +sessionStorage.getItem('timeExpiration');
      if (this._loggedIn === true && timeExpiration) {
        this.expirationTimerUnsubscribe.next();
        this.assingInterval(timeExpiration);
      }
    }, 600);
  }

  assingInterval(value: number) {
    const oneMinute = 60 * 1000;
    this.expirationTimer = timer((value * oneMinute) - oneMinute);
    this.endingTimer = timer(oneMinute);
    this.startExpirationTimer();
  }

  startExpirationTimer() {
    this.expirationTimer
      .takeUntil(this.expirationTimerUnsubscribe)
      .subscribe((value) => {
        this.modalSession = this._modalService.open(
          ExpirationSessionModalComponent,
          {
            closeOnBackdropClick: false,
            data: { expired: false, time: 1 }
          });
        this.modalSession.afterClosed().subscribe((result: any) => {
          if (result) {
            this._autoLoginClient.getProfile();
            this._authenticationSubject.next(true);
          }
        });
        this.startEndingTimer();
      });
  }

  startEndingTimer() {
    this.endingTimer
      .takeUntil(this.expirationTimerUnsubscribe)
      .subscribe((value) => {
        this.modalSession.close();
        this.modalSession = this._modalService.open(
          ExpirationSessionModalComponent,
          {
            closeOnBackdropClick: false,
            data: { expired: true }
          });
        this.modalSession.afterClosed().subscribe((result: any) => {
          this.logout();
        });
      });
  }

  unsubscribeTimer() {
    this.expirationTimerUnsubscribe.next();
  }

  getAuthenticationTypeResponse() {
    return this._api.get('api/Option/GetAuthenticationType')
      .pipe(map((response: GetAuthenticationTypeModel) => {
        this.authenticationConfig = {
          ...response,
          isAccounts2: response.SubTypeOAuth2Authentication === 'Accounts' && response.TypeIDPAuthentication === 'OAuth2'
        };
        this.authType = response.TypeIDPAuthentication;
        this.accounts2Service.setAuthenticationConfig(this.authenticationConfig)
        return this.authenticationConfig;
      }));
  }

  public init(params: any) {
    this.params = params;

    this.getConfigurationSummary().subscribe((summary) => {
      if (summary.isValid) {
        this.getAuthenticationTypeResponse().subscribe((data) => {
          this._authenticationTypeSubject.next(data.TypeIDPAuthentication);
          if (!this._loggedIn) {
            this.login();
          }
        });
      } else {
        this._router.navigate(['out-of-service']);
      }
    });
  }

  public GetAuthenticationType() {
    this.getAuthenticationTypeResponse().subscribe((data) => {
      this._authenticationTypeSubject.next(data.TypeIDPAuthentication);
    });
  }

  public GetAntiForgeryToken() {
    return this._api.get('api/configuration/antiforgerytoken')
      .subscribe((response) => {
      });
  }

  private getAuthInfo() {
    return this._api.get('api/auth/oauth/info').pipe(map((data) => {
      this.authStrategy = new AuthStrategy({
        baseUrl: data.identityServer,
        auth: {
          ...data,
          redirectUri: data.urls.redirectUri
        }
      });

      return data;
    }));
  }

  public async login() {
    switch (this.authType) {
      case 'OpenId':
        this.getAuthInfo().subscribe(() => {
          if (this.authStrategy.hasToken()) {
            this._loggedIn = true;
            this._api.setAuthenticationToken(this.authStrategy.getAccessToken());
            this._authenticationSubject.next(true);
          } else {
            this._api.setAuthenticationToken(this.authStrategy.getAccessToken());
            const code = this.authStrategy.queryString('code');
            if (code) {
              this.authStrategy.processSyncCode({ code, http: this._api }).then(({ profile, access_token, id_token }) => {
                this._loggedIn = true;
                this._api.setAuthenticationToken(access_token);
                this._authenticationSubject.next(true);
              })
            } else {
              this.authStrategy.authorize();
            }
          }
        });
        break;
      case 'OAuth2':
        switch (this.authenticationConfig.SubTypeOAuth2Authentication) {
          case 'Accounts':
            this.accounts2Service.getInstance().then(instance => {
              let code: string;
              if (this.authStrategy) {
                code = this.authStrategy.queryString('code');
              } else {
                code = extractSyncCode();
              }
              if (code) {
                instance.requestToken(code).then(({ error, expired, message, originalRoute }) => {
                  if (error || expired) {
                    instance.authorize();
                  } else {
                    this._loggedIn = true;
                    this._api.setAuthenticationToken(instance.getToken());
                    this._authenticationSubject.next(true);
                  }
                });
              } else {
                if (instance.hasToken) {
                  this._loggedIn = true;
                  this._api.setAuthenticationToken(instance.getToken());
                  this._authenticationSubject.next(true);
                } else {
                  instance.authorize();
                }
              }
            });
            break;
          case 'Bizagi':
            this._oAuthClient
              .login({
                queryParams: this.params,
                identityServer: this.params['identityServer'] || null
              }).then(() => {
                const token = this._oAuthClient.getToken();
                if (token) {
                  this._api.setAuthenticationToken(token);
                  // this.redirect();
                  this._authenticationSubject.next(true);
                }
              });
            break;
          default:
            alert('Unknown Authentication SubType: ' + this.authenticationConfig.SubTypeOAuth2Authentication);
            break;
        }
        break;
      case 'WSFederation':
        this._wsFederationClient.login()
          .subscribe({
            next: (res) => {
              if (res) {
                this._authenticationSubject.next(true);
              } else {
                this._wsFederationClient.submitLogin();
              }
            },
            error: (err) => {
              alert(err);
            }
          });
        break;
      case 'AutoLogin':
        if (AutoLoginStrategy.hasToken()) {
          this._api.setAuthorizationToken(AutoLoginStrategy.getAccessToken());
          this._autoLoginClient.getProfile()
            .subscribe((response) => {
              if (response && response.username) {
                this._loggedIn = true;
                this._authenticationSubject.next(true);
              } else {
                this._authenticationSubject.next(false);
                this._router.navigate(['login']);
              }
            });
        } else {
          this._loggedIn = false;
          this._authenticationSubject.next(false);
          this._autoLoginClient.configuration()
            .subscribe((response) => {
              if (response && response.configuration) {
                switch (response.configuration) {
                  case 'RegularLogin':
                    this._router.navigate(['login']);
                    break;
                  case 'AdminEmailLogin':
                    this._router.navigate(['send-admin-email']);
                    break;
                  default:
                    break;
                }
              }
            });
        }
        break;
      default:
        alert('Unknown Authentication Type');
        break;
    }

    this.sessionUpdate.pipe(throttleTime(1000)).subscribe(() => this.resetInterval());
  }

  public logout() {
    switch (this.authType) {
      case 'OpenId':
        this.authStrategy.logout(this._api);
        break;
      case 'OAuth2':
        switch (this.authenticationConfig.SubTypeOAuth2Authentication) {
          case 'Accounts':
            this.accounts2Service.getInstance().then(instance => {
              instance.logout();
            });
            break;
          case 'Bizagi':
            this._oAuthClient.logout();
            break;
        }
        break;
      case 'WSFederation':
        this._wsFederationClient.submitLogout();
        break;
      case 'AutoLogin':
        if (this._loggedIn) {
          this._autoLoginClient.logOut();
          this._loggedIn = false;
        }
        AutoLoginStrategy.removeLocalData();
        this._router.navigate(['login']);
        this.GetAntiForgeryToken();
        break;
      default:
        alert('Unknown Authentication Type');
        break;
    }

    this._authenticationTypeValidationService.forceToCloseToast();
    this.sessionUpdate = new Subject<void>();
  }

  public isAuthenticatedAsync(): Observable<boolean> {
    if (this._loggedIn) {
      return Observable.of(true);
    }
    return this._authenticationSubject.asObservable();
  }

  public getAuthenticationTypeAsync(): Observable<string> {
    return this._authenticationTypeSubject.asObservable();
  }

  public setAuthenticationSubject(isAuthenticated: boolean) {
    this._authenticationSubject.next(isAuthenticated);
  }

  public getProfile(): Observable<IUserProfile> {
    let profile: IUserProfile;

    const complete = (obs: Subscriber<IUserProfile>, _profile: IUserProfile) => {
      obs.next(_profile);
      obs.complete();
    };

    return new Observable((obs) => {
      switch (this.authType) {
        case 'OAuth2':
          switch (this.authenticationConfig.SubTypeOAuth2Authentication) {
            case 'Accounts':
              this.accounts2Service.getProfile().subscribe(_profile => {
                complete(obs, _profile);
              });
              break;
            default:
              profile = this._oAuthClient.getUserProfile();
              complete(obs, profile);
              break;
          }
          break;
        case 'WSFederation':
          profile = this._wsFederationClient.getUserProfile();
          complete(obs, profile);
          break;
        case 'AutoLogin':
          profile = this._autoLoginClient.getUserProfile();
          complete(obs, profile);
          break;
        case 'OpenId':
          profile = this.authStrategy.getProfile();
          complete(obs, profile);
          break;
        default:
          complete(obs, { name: '', email: '' });
          break;
      }
    });
  }

  public isEmptyWorkSpace(): Observable<boolean> {
    return this._api.get('api/Option/isEmptyWorkSpace').map((data) => {
      return data ? data.isEmptyWorkspace : false;
    });
  }

  public getConfigurationSummary(): Observable<IConfigurationSummary> {
    return this._api.get('api/configuration/summary').map((data) => {
      return data;
    });
  }

  public refreshToken(): Observable<any> {
    return this._autoLoginClient.refreshToken();
  }
}
